/**
 * Listagem de obras (HU 2521) — contrato de `GET /api/obras`. Condição e indicadores
 * ainda não têm fonte de dados: chegam `null` e a tela mostra "—" (Impasse 14).
 */
export interface ObraListaItem {
  id: string;
  codigo: string;
  embarcacao: { id: string; nome: string; acronimo?: string | null };
  statusObra: { id: string; codigo: string; descricao: string } | null;
  /** Códigos dos tipos de obra (100, 101...). */
  tiposObra: string[];
  /** `yyyy-MM-dd`. */
  inicio: string;
  /** `yyyy-MM-dd`. */
  fim: string;
  duracaoDias: number | null;
  condicao: string | null;
  realizadoBrlMilhoes: number | null;
  totalServicos: number | null;
  percentualServicosConcluidos: number | null;
  totalMateriais: number | null;
  percentualMateriaisEntregues: number | null;
  totalFacilidades: number | null;
  percentualFacilidadesConsumidas: number | null;
  empenhadoPercentual: number | null;
  comprometidoPercentual: number | null;
  estimadoPercentual: number | null;
  naoComprometidoPercentual: number | null;
}

export interface ObraTotalPorStatus {
  statusObraId: string | null;
  codigo: string | null;
  total: number;
}

export interface ObrasPage {
  items: ObraListaItem[];
  total: number;
  totaisPorStatus: ObraTotalPorStatus[];
}

/** Critérios da consulta; `statusObra`, `tipoObra` e `embarcacaoId` aceitam vários valores. */
export interface ObrasFilters {
  /** Códigos do enum de status da obra. */
  statusObra: string[];
  embarcacaoId: string[];
  /** Códigos do tipo de obra. */
  tipoObra: string[];
  /** `yyyy-MM-dd` ou vazio. */
  dataInicio: string;
  /** `yyyy-MM-dd` ou vazio. */
  dataFim: string;
}

export const EMPTY_OBRAS_FILTERS: ObrasFilters = {
  statusObra: [],
  embarcacaoId: [],
  tipoObra: [],
  dataInicio: "",
  dataFim: "",
};

export function hasObrasFilters(filters: ObrasFilters): boolean {
  return (
    filters.statusObra.length > 0 ||
    filters.embarcacaoId.length > 0 ||
    filters.tipoObra.length > 0 ||
    filters.dataInicio !== "" ||
    filters.dataFim !== ""
  );
}

/** Campos ordenáveis do contrato; Duração Total e Tipos de Obra não são ordenáveis (Impasse 48). */
export const OBRAS_SORT_FIELDS = ["id", "embarcacao", "statusObra", "inicio", "termino"] as const;

export type ObrasSortField = (typeof OBRAS_SORT_FIELDS)[number];

export function isObrasSortField(value: unknown): value is ObrasSortField {
  return (OBRAS_SORT_FIELDS as readonly unknown[]).includes(value);
}

export type ObrasSortDirection = "asc" | "desc";

export interface ObrasSort {
  field: ObrasSortField;
  direction: ObrasSortDirection;
}

/** Ordem inicial proposta na HU (a validar): ID decrescente. */
export const DEFAULT_OBRAS_SORT: ObrasSort = { field: "id", direction: "desc" };

/** Cada coluna de cards traz até 10 obras por vez (RN04). */
export const OBRAS_CARDS_PAGE_SIZE = 10;

export interface ObrasQueryState {
  status: "loading" | "success" | "error" | "forbidden";
  items: ObraListaItem[];
  total: number;
  errorMessage: string | null;
}

/** Colunas da tela de Obras, na ordem da tabela (a exportação traduz cada uma para a chave do core). */
export type ObrasColumnKey =
  | "id"
  | "embarcacao"
  | "statusObra"
  | "tipoObra"
  | "condicao"
  | "inicio"
  | "termino"
  | "duracao"
  | "realizado"
  | "totalServicos"
  | "percServicos"
  | "totalMateriais"
  | "percMateriais"
  | "totalFacilidades"
  | "percFacilidades"
  | "detalhamento";
